import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Search, FileText } from 'lucide-react';
import { LoadingSpinner } from '../../../../common';
import { AdminEmptyState } from '../../../ui';
import * as agentService from '../../../../../services/agentService';
import * as knowledgeService from '../../../../../services/knowledgeService';
import type { Agent } from '../../../../../types/agent';
import type { KnowledgeBaseResponse, KnowledgeBaseDetailType } from '../../../../../types/knowledgeBase';

interface AgentKnowledgeBaseProps {
  agent: Agent;
  onUpdate: () => void;
}

interface KnowledgeCardProps {
  kb: KnowledgeBaseResponse | KnowledgeBaseDetailType;
  isConnected?: boolean;
  onAdd?: () => void;
  onRemove?: () => void;
  onClick?: () => void;
}

const KnowledgeCard: React.FC<KnowledgeCardProps> = ({
  kb,
  isConnected,
  onAdd,
  onRemove,
  onClick,
}) => {
  const documents = 'documents' in kb ? kb.documents?.length || 0 : 0;

  return (
    <div
      className={`
        bg-[#1a1a1a] rounded-xl p-4
        border transition-all duration-200
        ${isConnected
          ? 'border-[#F4CC04]/30'
          : 'border-[#333333] hover:border-[#F4CC04] cursor-pointer'
        }
      `}
      onClick={!isConnected ? onAdd : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={isConnected ? 'cursor-pointer flex-1' : 'flex-1'}
          onClick={isConnected ? onClick : undefined}
        >
          <h3 className="text-white font-medium text-sm line-clamp-1">
            {kb.name}
          </h3>
          <p className="text-[#888888] text-xs mt-1 line-clamp-2">
            {kb.description || 'Sem descrição'}
          </p>
        </div>
        {isConnected ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className="p-1.5 text-[#888888] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Remover base de conhecimento"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <div className="p-1.5 text-[#F4CC04]">
            <Plus className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#333333]">
        <FileText className="w-3 h-3 text-[#888888]" />
        <span className="text-xs text-[#888888]">
          {documents} documento{documents !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export const AgentKnowledgeBase: React.FC<AgentKnowledgeBaseProps> = ({ agent, onUpdate }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseResponse[]>([]);
  const [connectedKnowledgeBases, setConnectedKnowledgeBases] = useState<KnowledgeBaseDetailType[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      // Never filter KB listing by the agent's workspace.
      // The admin should see KBs from their own workspace (TD admin → workspace 5,
      // workspace admin → their workspace). The agent's target workspace is irrelevant
      // here because KBs live in the creator's workspace, not the agent's client workspace.
      const [allKbResponse, connectedKbResponse] = await Promise.all([
        knowledgeService.listKnowledgeBases(undefined, undefined),
        agentService.getAgentKnowledgeBases(agent.id),
      ]);

      if (allKbResponse.success) {
        setKnowledgeBases(allKbResponse.data);
      }
      if (connectedKbResponse.success && connectedKbResponse.data) {
        setConnectedKnowledgeBases(connectedKbResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar bases de conhecimento');
    } finally {
      setIsLoading(false);
    }
  }, [agent.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleKnowledgeBaseAdd = async (kbId: number) => {
    try {
      setError(null);
      await agentService.addKnowledgeBase(agent.id, kbId);
      await fetchData();
      onUpdate();
      setShowAddModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao adicionar base de conhecimento');
    }
  };

  const handleKnowledgeBaseRemove = async (kbId: number) => {
    try {
      setError(null);
      await agentService.removeKnowledgeBase(agent.id, kbId);
      await fetchData();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao remover base de conhecimento');
    }
  };

  const filteredKnowledgeBases = knowledgeBases.filter(
    (kb) =>
      !connectedKnowledgeBases.some((connectedKb) => connectedKb.id === kb.id) &&
      (kb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        kb.description?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Bases de Conhecimento Conectadas</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="
            flex items-center gap-2 px-3 py-2
            bg-[#F4CC04] hover:bg-[#D4B104]
            text-black text-sm font-medium
            rounded-lg transition-colors
          "
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Connected Knowledge Bases */}
      {connectedKnowledgeBases.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connectedKnowledgeBases.map((kb) => (
            <KnowledgeCard
              key={kb.id}
              kb={kb}
              isConnected
              onRemove={() => handleKnowledgeBaseRemove(kb.id)}
              onClick={() => navigate(`/admin/databases/${kb.id}`)}
            />
          ))}
        </div>
      ) : (
        <AdminEmptyState
          title="Nenhuma Base de Conhecimento Conectada"
          description="Adicione bases de conhecimento para melhorar as capacidades do agente"
          action={
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
            >
              Adicionar Base de Conhecimento
            </button>
          }
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() => setShowAddModal(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-[#2a2a2a] rounded-2xl p-6 z-50 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Adicionar Base de Conhecimento</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Buscar bases de conhecimento..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="
                  w-full px-4 py-2.5 pl-10
                  bg-[#1a1a1a] border border-[#333333]
                  text-white placeholder-[#888888] text-sm
                  rounded-lg
                  focus:outline-none focus:border-[#F4CC04]
                "
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {filteredKnowledgeBases.length > 0 ? (
                filteredKnowledgeBases.map((kb) => (
                  <KnowledgeCard
                    key={kb.id}
                    kb={kb}
                    onAdd={() => handleKnowledgeBaseAdd(kb.id)}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#888888] text-sm">Nenhuma base de conhecimento encontrada</p>
                  <p className="text-[#666666] text-xs mt-1">Tente ajustar os termos de busca</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AgentKnowledgeBase;
